import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography, Button, Skeleton } from "@mui/material";

interface EntityAboutProps {
  bio?: string;
  isLoading: boolean;
}

const aboutTextStyle = {
  color: "var(--Dark-Mode---25, #5E5E5E)",
  fontFamily: "Montserrat",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "20px",
};

const EntityAbout = ({ bio, isLoading }: EntityAboutProps) => {
  const { t } = useTranslation("profiles", { keyPrefix: "about" });
  const [isExpanded, setIsExpanded] = useState(false);
  const [isClamped, setIsClamped] = useState(false);
  const textRef = useRef(null);

  useEffect(() => {
    const textElement = textRef.current;
    if (textElement) {
      const { clientHeight, scrollHeight } = textElement;
      if (clientHeight < scrollHeight) {
        setIsClamped(true);
      }
    }
  }, [bio]);

  const toggleExpansion = () => {
    setIsExpanded((prev) => !prev);
  };

  if (isLoading) {
    return (
      <Box>
        <Typography
          sx={{
            color: "var(--black-100, #1C1C1C)",
            fontFamily: "Montserrat",
            fontSize: "12px",
            fontStyle: "normal",
            fontWeight: 600,
            lineHeight: "20px",
          }}
        >
          {t("about")}
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          <Skeleton variant="text" height={14} width="100%" />
          <Skeleton variant="text" height={14} width="100%" />
          <Skeleton variant="text" height={14} width="100%" />
          <Skeleton variant="text" height={14} width="35%" />
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Typography
        sx={{
          color: "var(--black-100, #1C1C1C)",
          fontFamily: "Montserrat",
          fontSize: "12px",
          fontStyle: "normal",
          fontWeight: 600,
          lineHeight: "20px",
        }}
      >
        {t("about")}
      </Typography>

      {!bio ? (
        <Typography sx={aboutTextStyle}>{t("noAbout")}</Typography>
      ) : (
        <>
          <Typography
            ref={textRef}
            sx={{
              ...aboutTextStyle,
              display: "-webkit-box",
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              WebkitLineClamp: isExpanded ? "none" : 4,
            }}
          >
            {bio}
          </Typography>
          {isClamped && (
            <Button
              onClick={toggleExpansion}
              variant="text-md"
              sx={{
                marginTop: "10px",
                padding: 0,
                "&:hover": {
                  background: "none",
                },
              }}
            >
              {isExpanded ? t("showLess") : t("showMore")}
            </Button>
          )}
        </>
      )}
    </Box>
  );
};

export default EntityAbout;
